/**
 * useFlowChatListRows — the single flat, newest-first merge of live chat
 * sessions and server-persisted conversation history (bead
 * terra-design-v2-chat-yub.2, Codex spec 1). Extracted from
 * `FlowChatSessionList.tsx`'s own `rows` useMemo (round-3 QA fix, item 6:
 * merging `useKeystoneConversations()` into "All chats") so V1
 * (`FlowChatSessionList`) and V2 (`shell-next/chat/ChatSessionListV2`)
 * consume the SAME derivation (coding-standards.md: "Derivation belongs in
 * a hook shared by V1 and V2, in `EnrollQueue/`"). Do NOT re-derive this
 * merge/sort/dedupe anywhere else.
 *
 * A persisted conversation whose id already matches a LIVE session's
 * `conversationId` is dropped (the live entry is always
 * fresher/streaming-aware); everything else sorts by effective timestamp
 * descending across both sources — never two separately-sorted buckets
 * concatenated in a fixed order.
 */
import { useMemo } from 'react';
import { toDateString } from '../../account-view/keystone/ConversationList';
import { useKeystoneConversations } from '../../account-view/keystone/useKeystoneConversations';
import type { FlowChatSessionSummary } from './FlowChatSessionsProvider';
import type { Conversation } from '@clay-run/keystone-contract';

/**
 * Effective sort timestamp for a persisted conversation: `updatedAt ??
 * createdAt`, parsed; `0` when neither parses (malformed or both null).
 * Note the `??` only falls back on `null`/`undefined` — a malformed but
 * non-null `updatedAt` (e.g. `'invalid'`) is NOT replaced by `createdAt`,
 * it just parses to `0` (Codex spec 6). Kept here (not exported from
 * `ConversationList.tsx`, which doesn't export it) as the one shared site —
 * V1's row and this hook both import THIS, never re-declare it.
 */
export function effectiveTimestamp(conv: Pick<Conversation, 'updatedAt' | 'createdAt'>): number {
  const dateStr = toDateString(conv.updatedAt ?? conv.createdAt ?? null);
  if (!dateStr) return 0;
  const parsed = new Date(dateStr).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export type ListRow =
  | { kind: 'live'; timestamp: number; entry: FlowChatSessionSummary; title: string | null }
  | { kind: 'persisted'; timestamp: number; conversation: Conversation };

export type SessionBadge = 'running' | 'ready' | 'stopped';

/**
 * Status precedence shared by V1's `FlowChatSessionRow` and V2's chat-local
 * row (coding-standards.md: "share it, do not re-derive" the badge logic).
 * `isRunning` is the live stream's own flag, read by the caller via
 * `useFlowChatSessionStream` — never mirrored here.
 */
export function deriveSessionBadge(
  entry: Pick<FlowChatSessionSummary, 'stopped' | 'createStatus'>,
  isRunning: boolean | undefined,
): SessionBadge {
  if (entry.stopped) return 'stopped';
  if (entry.createStatus === 'creating' || isRunning) return 'running';
  return 'ready';
}

/**
 * `useFlowChatListRows(sessions, includePersisted)` — always subscribes to
 * `useKeystoneConversations()` (Rule of Hooks; `staleTime: Infinity` makes
 * the extra subscription cheap when unused), but only merges its rows in
 * when `includePersisted` is true.
 */
export function useFlowChatListRows(
  sessions: FlowChatSessionSummary[],
  includePersisted: boolean,
): ListRow[] {
  const { data: persistedConversations } = useKeystoneConversations();

  const liveConversationIds = useMemo(
    () => new Set(sessions.map((s) => s.conversationId).filter((id): id is string => id !== null)),
    [sessions],
  );

  const persistedTitleById = useMemo(
    () => new Map((persistedConversations ?? []).map((c) => [c.id, c.title])),
    [persistedConversations],
  );

  return useMemo(() => {
    // `title`: the live session's persisted title once its conversation exists
    // server-side (what the row displays), else null.
    const liveRows: ListRow[] = sessions.map((entry) => ({
      kind: 'live' as const,
      timestamp: entry.lastActivityAt,
      entry,
      title: (entry.conversationId ? persistedTitleById.get(entry.conversationId) : null) ?? null,
    }));

    const persistedRows: ListRow[] = includePersisted
      ? (persistedConversations ?? [])
          .filter((c) => !liveConversationIds.has(c.id))
          .map((c) => ({ kind: 'persisted' as const, timestamp: effectiveTimestamp(c), conversation: c }))
      : [];

    return [...liveRows, ...persistedRows].sort((a, b) => b.timestamp - a.timestamp);
  }, [sessions, persistedConversations, persistedTitleById, liveConversationIds, includePersisted]);
}

/**
 * Search match for a chat row (case-insensitive, trimmed). Matches the title
 * as the row displays it — a live session's persisted title when known, else
 * its pending first message; a persisted row's own title — and the account
 * name.
 */
export function matchesChatRow(row: ListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack =
    row.kind === 'live'
      ? [row.title ?? row.entry.pendingFirstMessage, row.entry.accountName]
      : [row.conversation.title, row.conversation.accountName];
  return haystack.some(v => !!v && v.toLowerCase().includes(q));
}
